/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { lazy, useEffect, useState } from "react";
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { SIGN_OUT_PATH } from "./auth/useSignOut";
import { Deferred } from "./components/ui/Deferred";
import { ToastContainer } from "./components/ui/Toast";
import { Spinner } from "./components/ui/Spinner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider, useToast } from "./contexts/ToastContext";
import { Login } from "./routes/Login";
import { PendingAccess } from "./routes/PendingAccess";
import { Shell } from "./routes/Shell";
import { Inbox } from "./routes/Inbox";
import { Runs } from "./routes/Runs";
import { SchedulingPool } from "./routes/SchedulingPool";
import { ManualRun } from "./routes/ManualRun";
import { StreamDetail } from "./routes/StreamDetail";
import { NotFound } from "./routes/NotFound";
import { Demo } from "./routes/Demo";
import { Families } from "./routes/Families";
import { CareTeam } from "./routes/CareTeam";
import { Insurance } from "./routes/Insurance";
import { Duplicates } from "./routes/Duplicates";
import { DuplicateDetail } from "./routes/DuplicateDetail";
import { FamilyDetail } from "./routes/FamilyDetail";
import { Access } from "./routes/Access";
import { AccountDetail } from "./routes/AccountDetail";
import { ReferralOrganizations } from "./routes/ReferralOrganizations";
import { ReferralOrganizationDetail } from "./routes/ReferralOrganizationDetail";
import { ReferralProviders } from "./routes/ReferralProviders";
import { ReferralProviderDetail } from "./routes/ReferralProviderDetail";
import { ReferralDuplicates } from "./routes/ReferralDuplicates";
import { ReferralDuplicateDetail } from "./routes/ReferralDuplicateDetail";
import { ReferralFaxes } from "./routes/ReferralFaxes";
import { Referrals } from "./routes/Referrals";
import { ReferralNew } from "./routes/ReferralNew";
import { ReferralFaxDetail } from "./routes/ReferralFaxDetail";

const ReferralDetail = lazy(() =>
  import("./routes/ReferralDetail").then((module) => ({ default: module.ReferralDetail })),
);
const MessagingSchedules = lazy(() =>
  import("./routes/MessagingSchedules").then((module) => ({ default: module.MessagingSchedules })),
);
const MessagingSchedule = lazy(() =>
  import("./routes/MessagingSchedule").then((module) => ({ default: module.MessagingSchedule })),
);
const MessagingTemplates = lazy(() =>
  import("./routes/MessagingTemplates").then((module) => ({ default: module.MessagingTemplates })),
);
const MessagingTemplate = lazy(() =>
  import("./routes/MessagingTemplate").then((module) => ({ default: module.MessagingTemplate })),
);
const SendingHours = lazy(() =>
  import("./routes/SendingHours").then((module) => ({ default: module.SendingHours })),
);

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  if (session.state === "loading") {
    return (
      <div className="grid h-screen place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (session.state === "anonymous") {
    return <Navigate to="/login" replace />;
  }
  if (session.user.roles.length === 0) {
    return <PendingAccess />;
  }
  return <>{children}</>;
}

function SignOut() {
  const { session, logout } = useAuth();
  useEffect(() => {
    void logout();
  }, [logout]);
  return session.state === "anonymous" ? (
    <Navigate to="/login" replace />
  ) : (
    <div className="grid h-screen place-items-center">
      <Spinner size="lg" />
    </div>
  );
}

function Toasts() {
  const { toasts, dismissToast } = useToast();
  return <ToastContainer toasts={toasts} onDismiss={dismissToast} />;
}

function appRoutes() {
  return createRoutesFromElements(
  <>
    <Route path="/login" element={<Login />} />
  <Route path={SIGN_OUT_PATH} element={<SignOut />} />
    <Route
      element={
        <RequireAuth>
          <Shell />
        </RequireAuth>
      }
    >
      <Route path="/" element={<Navigate to="/inbox" replace />} />

      {/* Inbox — the unified work queue (live) */}
      <Route path="/inbox" element={<Inbox />} />
      <Route path="/inbox/claimed" element={<Inbox view="claimed" />} />

      {/* Agents — the autonomous workforce */}
      <Route path="/runs" element={<Navigate to="/agents/runs" replace />} />
      <Route path="/agents" element={<Navigate to="/agents/runs" replace />} />
      <Route path="/agents/runs" element={<Runs />} />
      <Route path="/streams/:id" element={<StreamDetail />} />
      <Route path="/agents/scheduling" element={<SchedulingPool />} />
      <Route path="/agents/manual-run" element={<ManualRun />} />

      {/* Care — the people */}
      <Route path="/care" element={<Navigate to="/care/families" replace />} />
      <Route path="/accounts" element={<Navigate to="/care/families" replace />} />
      <Route path="/accounts/:id" element={<AccountDetail />} />
      <Route path="/care/families" element={<Families />} />
      <Route path="/care/duplicates" element={<Duplicates />} />
      <Route path="/care/duplicates/:id" element={<DuplicateDetail />} />
      <Route path="/care/families/:id" element={<FamilyDetail />} />
      <Route path="/care/patients" element={<Navigate to="/care/families" replace />} />
      <Route
        path="/care/patients/:id"
        element={<Navigate to="/care/families" replace />}
      />
      <Route path="/care/providers" element={<Navigate to="/care/team" replace />} />
      <Route path="/care/team" element={<CareTeam />} />
      <Route path="/care/insurance" element={<Insurance />} />

      {/* Referrals — the external registry */}
      <Route path="/referrals" element={<Referrals />} />
      <Route path="/referrals/new" element={<ReferralNew />} />
      <Route
        path="/referrals/:id"
        element={
          <Deferred>
            <ReferralDetail />
          </Deferred>
        }
      />
      <Route path="/referrals/organizations" element={<ReferralOrganizations />} />
      <Route path="/referrals/organizations/:id" element={<ReferralOrganizationDetail />} />
      <Route path="/referrals/providers" element={<ReferralProviders />} />
      <Route path="/referrals/providers/:id" element={<ReferralProviderDetail />} />
      <Route path="/referrals/duplicates" element={<ReferralDuplicates />} />
      <Route path="/referrals/duplicates/:id" element={<ReferralDuplicateDetail />} />
      <Route path="/referrals/faxes" element={<ReferralFaxes />} />
      <Route path="/referrals/faxes/:id" element={<ReferralFaxDetail />} />

      {/* Messaging — what goes out on its own, and when */}
      <Route path="/messaging" element={<Navigate to="/messaging/schedules" replace />} />
      <Route
        path="/messaging/schedules"
        element={
          <Deferred>
            <MessagingSchedules />
          </Deferred>
        }
      />
      <Route
        path="/messaging/schedules/:key"
        element={
          <Deferred>
            <MessagingSchedule />
          </Deferred>
        }
      />
      <Route
        path="/messaging/templates"
        element={
          <Deferred>
            <MessagingTemplates />
          </Deferred>
        }
      />
      <Route
        path="/messaging/templates/:id"
        element={
          <Deferred>
            <MessagingTemplate />
          </Deferred>
        }
      />
      <Route
        path="/messaging/sending-hours"
        element={
          <Deferred>
            <SendingHours />
          </Deferred>
        }
      />

      {/* Admin — governance the agents obey */}
      <Route path="/admin" element={<Navigate to="/admin/access" replace />} />
      <Route path="/admin/demo" element={<Demo />} />
      <Route path="/admin/outreach-policy" element={<Navigate to="/messaging/sending-hours" replace />} />
      <Route path="/admin/message-schedule" element={<Navigate to="/messaging/schedules" replace />} />
    <Route path="/admin/payers" element={<Navigate to="/care/insurance" replace />} />
      <Route path="/payers" element={<Navigate to="/care/insurance" replace />} />
      <Route path="/admin/access" element={<Access />} />

      <Route path="*" element={<NotFound />} />
    </Route>
  </>,
  );
}

export function App() {
  const [router] = useState(() => createBrowserRouter(appRoutes()));
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toasts />
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
